/* eslint-disable react/display-name */
import { memo } from 'react';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { Button, PageTitle } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { Text, TextArea } from '@dec/shared';
import { HookForm } from '@dec/shared';
import { BtnGroup } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { Col } from '@dec/shared';
import { Row } from '@dec/shared';
import { ConfirmDialog } from 'app/core/dialog';
import { systemMessage } from 'app/core/system-message';
import { getSystemMessage } from 'app/core/sytem-message';
import { notificationSuccess as notifySuccess } from 'app/core/toast';
import { useSpinnerActions } from 'app/layouts/store';
import { useGetTaxReturnById } from 'app/modules/Reports/TaxReturnList/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { notification } from '@/app/core/notifications/notification';

import { label as labelCommon, messages as messagesCommon } from '../../../../translation';
import service from '../../services/taxReturnEvaluation.service';
import { label, title } from '../../translation';
import { type ApproveTaxReturnDto } from '../../types';

import validations from './validations-approve';

interface Props {}

export const ApproveTaxreturn = memo((props: Props) => {
  const navigate = useNavigate();
  const { showSpinner, hideSpinner } = useSpinnerActions();
  const { id } = useParams<Record<string, string>>();
  const { t } = useTranslation();
  const { loading, taxreturn } = useGetTaxReturnById(id ?? '');

  if (loading) {
    return <></>;
  }

  const onSubmit = async (values: ApproveTaxReturnDto) => {
    const catalog = await getSystemMessage(systemMessage.taxreturnAmendedApproval);

    if (!catalog) {
      const message = messagesCommon('errorMessageSystem').replace(
        '${code}',
        systemMessage.taxreturnAmendedApproval
      );
      notification.error(message);
      return;
    }

    const ok = await ConfirmDialog({
      titleModal: title(t, 'dialog-confirm'),
      message: catalog?.additionalFields?.message as string,
      okButton: labelCommon('ok'),
      cancelButton: labelCommon('cancel')
    });
    if (!ok) {
      return;
    }

    values.id = id ?? '';
    showSpinner();
    try {
      const { error } = (await service.approve(values)) as any;
      if (error) {
        return;
      }
      notifySuccess(t('taxreturn-evaluation.messages.approved'));
      navigate('/statements/taxreturn-evaluation');
    } finally {
      hideSpinner();
    }
  };

  return (
    <LoadingOverlay active={loading} spinner text={messagesCommon('pleaseWait')}>
      <PageTitle title={title(t, 'approve')} />
      <HookForm onSubmit={onSubmit} validations={validations}>
        <Row>
          <Col>
            <Text
              name="formName"
              defaultValue={`${taxreturn?.formVersion?.form?.code} - ${taxreturn?.formVersion?.form?.shortName}`}
              label={label(t, 'form')}
              readonly={true}
            />
          </Col>
          <Col>
            <Text
              name="taxpayerName"
              defaultValue={taxreturn?.taxpayer?.name}
              label={label(t, 'taxpayer')}
              readonly={true}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <TextArea rows={4} label={label(t, 'comments')} name="comments" />
          </Col>
        </Row>

        <BtnToolbar>
          <BtnGroup>
            <>
              <Button
                action={ButtonAction.cancel}
                variant="secondary"
                onClick={() => navigate(`/statements/taxreturn-evaluation/${id}`)}
              />
            </>
          </BtnGroup>
          <BtnGroup>
            <Button
              action={ButtonAction.save}
              text={label(t, 'approve')}
              variant="primary"
              type="submit"
            />
          </BtnGroup>
        </BtnToolbar>
      </HookForm>
    </LoadingOverlay>
  );
});
